import { useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import { Textarea } from "../ui/textarea";
import { ScrollArea } from "../ui/scroll-area";
import { Slider } from "../ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { 
  Check, Eye, Palette, Mail, Gift, Bell, Calendar, 
  ShoppingBag, Heart, MessageSquare, Newspaper, Tag,
  ChevronLeft, Upload, Sparkles, RotateCw, Maximize2,
  AlignLeft, AlignCenter, AlignRight, Settings2
} from "lucide-react";

const CATEGORY_INFO = {
  newsletter: { label: "Newsletter", icon: Newspaper, color: "bg-blue-500" },
  promocion: { label: "Promoción", icon: Gift, color: "bg-green-500" },
  anuncio: { label: "Anuncio", icon: Bell, color: "bg-red-500" },
  bienvenida: { label: "Bienvenida", icon: Sparkles, color: "bg-purple-500" },
  evento: { label: "Evento", icon: Calendar, color: "bg-violet-500" },
  recordatorio: { label: "Recordatorio", icon: Bell, color: "bg-amber-500" },
  producto: { label: "Producto", icon: ShoppingBag, color: "bg-emerald-500" },
  agradecimiento: { label: "Gracias", icon: Heart, color: "bg-pink-500" },
  encuesta: { label: "Encuesta", icon: MessageSquare, color: "bg-sky-500" },
  noticia: { label: "Noticia", icon: Newspaper, color: "bg-indigo-500" },
  otros: { label: "Otros", icon: Mail, color: "bg-gray-500" }
};

// Parse image settings from value (format: url|width|height|align|rotate or just url)
function parseImageValue(val) {
  if (!val) return { url: "", width: 100, height: "auto", align: "center", rotate: 0 };
  if (!val.includes("|")) return { url: val, width: 100, height: "auto", align: "center", rotate: 0 };
  const parts = val.split("|");
  return {
    url: parts[0] || "",
    width: parseInt(parts[1]) || 100,
    height: parts[2] || "auto",
    align: parts[3] || "center",
    rotate: parseInt(parts[4]) || 0
  };
}

// Build image value string with settings
function buildImageValue(settings) {
  const s = settings;
  if (s.width === 100 && s.height === "auto" && s.align === "center" && s.rotate === 0) {
    return s.url;
  }
  return `${s.url}|${s.width}|${s.height}|${s.align}|${s.rotate}`;
}

// Mini preview renderer
function TemplatePreview({ template, scale = 0.2 }) {
  const html = template.html || "";
  const colorScheme = template.color_scheme || "#0C314C";
  
  let previewHtml = html
    .replace(/\{\{#if\s+\w+\}\}[\s\S]*?\{\{\/if\}\}/g, "")
    .replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const varDef = template.variables?.find(v => v.key === key);
      if (varDef?.default) return varDef.default;
      if (key.includes("titulo")) return "Título de Ejemplo";
      if (key.includes("contenido")) return "<p>Contenido de ejemplo...</p>";
      if (key.includes("empresa")) return "Tu Empresa";
      if (key.includes("color")) return colorScheme;
      return "";
    });

  return (
    <div 
      className="origin-top-left overflow-hidden bg-white rounded"
      style={{ transform: `scale(${scale})`, width: `${100/scale}%`, height: 600 * scale }}
    >
      <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
    </div>
  );
}

// Image Variable Input Component (separate to allow hooks)
function ImageVariableInput({ variableKey, label, value, onChange, onImageUpload }) {
  const settings = parseImageValue(value);
  const [showSettings, setShowSettings] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  useEffect(() => {
    setLocalSettings(parseImageValue(value));
  }, [value]);

  const handleUrlChange = (newUrl) => {
    const newSettings = { ...localSettings, url: newUrl };
    setLocalSettings(newSettings);
    onChange(variableKey, buildImageValue(newSettings));
  };

  // Update settings AND immediately apply to parent for real-time preview
  const handleSettingsChange = (newSettings) => {
    const updatedSettings = { ...localSettings, ...newSettings };
    setLocalSettings(updatedSettings);
    // Immediately update parent so preview updates in real-time
    onChange(variableKey, buildImageValue(updatedSettings));
  };

  const getPreviewStyle = () => {
    const style = { maxWidth: `${localSettings.width}%`, transform: `rotate(${localSettings.rotate}deg)` };
    if (localSettings.height !== "auto") {
      style.height = `${localSettings.height}px`;
      style.objectFit = "cover";
    }
    return style;
  };

  const getContainerStyle = () => ({
    textAlign: localSettings.align,
    display: "flex",
    justifyContent: localSettings.align === "left" ? "flex-start" : 
                    localSettings.align === "right" ? "flex-end" : "center"
  });

  return (
    <div className="space-y-2">
      <Label className="text-sm">{label}</Label>
      <div className="flex gap-2">
        <Input 
          value={settings.url || ""} 
          onChange={(e) => handleUrlChange(e.target.value)}
          placeholder="URL de la imagen o sube una"
          className="flex-1"
        />
        <Button variant="outline" size="sm" asChild className="shrink-0">
          <label className="cursor-pointer flex items-center gap-1">
            <Upload className="w-4 h-4" />
            <span className="text-xs">Subir</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => onImageUpload(variableKey, e)} />
          </label>
        </Button>
      </div>
      
      {settings.url && (
        <div className="space-y-3 p-3 bg-slate-50 rounded-lg border">
          <div style={getContainerStyle()} className="bg-white p-2 rounded border min-h-[60px]">
            <img src={settings.url} alt="Preview" className="rounded object-contain" style={getPreviewStyle()} />
          </div>
          
          <Button variant="outline" size="sm" onClick={() => setShowSettings(!showSettings)} className="w-full gap-2">
            <Settings2 className="w-4 h-4" />
            {showSettings ? "Ocultar opciones" : "Ajustar tamaño y posición"}
          </Button>
          
          {showSettings && (
            <div className="space-y-4 pt-2">
              {/* Width */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1"><Maximize2 className="w-3 h-3" /> Ancho</Label>
                  <span className="text-xs text-slate-500">{localSettings.width}%</span>
                </div>
                <Slider value={[localSettings.width]} onValueChange={([val]) => handleSettingsChange({ width: val })} min={20} max={100} step={5} />
              </div>

              {/* Height */}
              <div className="space-y-2">
                <Label className="text-xs">Altura</Label>
                <Select value={localSettings.height} onValueChange={(val) => handleSettingsChange({ height: val })}>
                  <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Automática</SelectItem>
                    <SelectItem value="50">50px - Pequeña</SelectItem>
                    <SelectItem value="80">80px - Mediana</SelectItem>
                    <SelectItem value="120">120px - Grande</SelectItem>
                    <SelectItem value="200">200px - Muy grande</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Alignment */}
              <div className="space-y-2">
                <Label className="text-xs">Alineación</Label>
                <div className="flex gap-1">
                  <Button variant={localSettings.align === "left" ? "default" : "outline"} size="sm" onClick={() => handleSettingsChange({ align: "left" })} className="flex-1 h-8"><AlignLeft className="w-4 h-4" /></Button>
                  <Button variant={localSettings.align === "center" ? "default" : "outline"} size="sm" onClick={() => handleSettingsChange({ align: "center" })} className="flex-1 h-8"><AlignCenter className="w-4 h-4" /></Button>
                  <Button variant={localSettings.align === "right" ? "default" : "outline"} size="sm" onClick={() => handleSettingsChange({ align: "right" })} className="flex-1 h-8"><AlignRight className="w-4 h-4" /></Button>
                </div>
              </div>

              {/* Rotation */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs flex items-center gap-1"><RotateCw className="w-3 h-3" /> Rotación</Label>
                  <span className="text-xs text-slate-500">{localSettings.rotate}°</span>
                </div>
                <Slider value={[localSettings.rotate]} onValueChange={([val]) => handleSettingsChange({ rotate: val })} min={-180} max={180} step={5} />
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" onClick={() => handleSettingsChange({ rotate: 0 })} className="flex-1 h-7 text-xs">0°</Button>
                  <Button variant="outline" size="sm" onClick={() => handleSettingsChange({ rotate: 90 })} className="flex-1 h-7 text-xs">90°</Button>
                  <Button variant="outline" size="sm" onClick={() => handleSettingsChange({ rotate: 180 })} className="flex-1 h-7 text-xs">180°</Button>
                  <Button variant="outline" size="sm" onClick={() => handleSettingsChange({ rotate: -90 })} className="flex-1 h-7 text-xs">-90°</Button>
                </div>
              </div>

              <p className="text-xs text-center text-green-600 pt-2">
                ✓ Los cambios se aplican automáticamente
              </p>
            </div>
          )}

          <Button variant="ghost" size="sm" onClick={() => onChange(variableKey, "")} className="w-full text-red-500 hover:text-red-700 hover:bg-red-50">
            Eliminar imagen
          </Button>
        </div>
      )}
    </div>
  );
}

// Variable input component
function VariableInput({ variable, value, onChange, onImageUpload }) {
  const { key, label, type, required, default: defaultValue } = variable;
  
  if (type === "color") {
    return (
      <div className="space-y-1">
        <Label className="text-sm">{label} {required && <span className="text-red-500">*</span>}</Label>
        <div className="flex gap-2 items-center">
          <input type="color" value={value || defaultValue || "#0C314C"} onChange={(e) => onChange(key, e.target.value)} className="w-10 h-10 rounded cursor-pointer border" />
          <Input value={value || defaultValue || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={defaultValue} className="flex-1" />
        </div>
      </div>
    );
  }
  
  if (type === "image") {
    return <ImageVariableInput variableKey={key} label={label} value={value} onChange={onChange} onImageUpload={onImageUpload} />;
  }
  
  if (type === "richtext") {
    return (
      <div className="space-y-1">
        <Label className="text-sm">{label} {required && <span className="text-red-500">*</span>}</Label>
        <Textarea value={value || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={defaultValue || "Escribe aquí..."} rows={4} />
      </div>
    );
  }
  
  if (type === "url") {
    return (
      <div className="space-y-1">
        <Label className="text-sm">{label} {required && <span className="text-red-500">*</span>}</Label>
        <Input type="url" value={value || defaultValue || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={defaultValue || "https://"} />
      </div>
    );
  }
  
  if (type === "checkbox") {
    return (
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={value ?? defaultValue ?? false} onChange={(e) => onChange(key, e.target.checked)} className="rounded" />
        <Label className="text-sm cursor-pointer">{label}</Label>
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      <Label className="text-sm">{label} {required && <span className="text-red-500">*</span>}</Label>
      <Input value={value || ""} onChange={(e) => onChange(key, e.target.value)} placeholder={defaultValue || ""} />
    </div>
  );
}

export default function PlantillasGallery({ plantillas = [], onSelect, onClose, onImageUpload }) {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [variableValues, setVariableValues] = useState({});
  const [showCustomizer, setShowCustomizer] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  
  const categories = plantillas.reduce((acc, p) => {
    const cat = p.categoria || "otros";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(p);
    return acc;
  }, {});
  
  const filteredPlantillas = selectedCategory === "all" ? plantillas : categories[selectedCategory] || [];
  
  // Update preview when variables change
  useEffect(() => {
    if (!selectedTemplate) return;
    
    let html = selectedTemplate.html || "";
    
    // Handle conditional blocks
    html = html.replace(/\{\{#if\s+(\w+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (match, key, content) => {
      const value = variableValues[key];
      const urlPart = value?.split?.("|")?.[0];
      if (urlPart && urlPart !== "" && value !== false) {
        return content;
      }
      return "";
    });
    
    // Replace variables - for images, include full value with style params
    html = html.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      const value = variableValues[key];
      const varDef = selectedTemplate.variables?.find(v => v.key === key);
      
      // For image variables, return the full value including style params
      // This will be processed later to apply styles
      if (varDef?.type === "image" && value) {
        return value; // Return full value like "url|80|auto|center|0"
      }
      
      if (value !== undefined && value !== "") return value;
      return varDef?.default || "";
    });
    
    // Post-process: Find img tags and apply image styles
    // Match img tags where src contains "|" (has style parameters)
    html = html.replace(/<img([^>]*?)src="([^"]+)"([^>]*?)>/gi, (match, before, srcValue, after) => {
      // Check if srcValue contains style parameters (has |)
      if (!srcValue.includes("|")) {
        // No style params, return as-is
        return match;
      }
      
      const parts = srcValue.split("|");
      const url = parts[0];
      const width = parseInt(parts[1]) || 100;
      const height = parts[2] || "auto";
      const align = parts[3] || "center";
      const rotate = parseInt(parts[4]) || 0;
      
      // Build image style
      let imgStyle = `max-width: ${width}%; display: block;`;
      if (height !== "auto") {
        imgStyle += ` height: ${height}px; object-fit: cover;`;
      }
      if (rotate !== 0) {
        imgStyle += ` transform: rotate(${rotate}deg);`;
      }
      
      // Handle alignment with margin
      if (align === "center") {
        imgStyle += " margin-left: auto; margin-right: auto;";
      } else if (align === "right") {
        imgStyle += " margin-left: auto; margin-right: 0;";
      } else {
        imgStyle += " margin-left: 0; margin-right: auto;";
      }
      
      // Build the new img tag
      return `<img${before}src="${url}" style="${imgStyle}"${after}>`;
    });
    
    setPreviewHtml(html);
  }, [selectedTemplate, variableValues]);
  
  const handleSelectTemplate = (template) => {
    setSelectedTemplate(template);
    const defaults = {};
    template.variables?.forEach(v => {
      if (v.default !== undefined) defaults[v.key] = v.default;
    });
    setVariableValues(defaults);
    setShowCustomizer(true);
  };
  
  const handleVariableChange = (key, value) => {
    setVariableValues(prev => ({ ...prev, [key]: value }));
  };
  
  const handleConfirm = () => {
    if (!selectedTemplate) return;
    
    const missingRequired = selectedTemplate.variables?.filter(v => v.required && !variableValues[v.key]);
    if (missingRequired?.length > 0) {
      alert(`Por favor completa los campos requeridos: ${missingRequired.map(v => v.label).join(", ")}`);
      return;
    }
    
    onSelect({
      plantilla_id: selectedTemplate.id,
      plantilla_nombre: selectedTemplate.nombre,
      contenido_html: previewHtml,
      variables: variableValues
    });
  };
  
  const handleImageUploadForVar = async (key, event) => {
    if (onImageUpload) {
      const url = await onImageUpload(event);
      if (url) handleVariableChange(key, url);
    }
  };
  
  if (showCustomizer && selectedTemplate) {
    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center gap-2 pb-4 border-b">
          <Button variant="ghost" size="sm" onClick={() => setShowCustomizer(false)}>
            <ChevronLeft className="w-4 h-4 mr-1" /> Volver
          </Button>
          <div className="flex-1">
            <h3 className="font-semibold">{selectedTemplate.nombre}</h3>
            <p className="text-sm text-gray-500">{selectedTemplate.descripcion}</p>
          </div>
        </div>
        
        <div className="flex-1 grid grid-cols-2 gap-4 pt-4 min-h-0">
          <ScrollArea className="h-[500px] pr-4">
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2"><Palette className="w-4 h-4" /> Personalizar Contenido</h4>
              {selectedTemplate.variables?.map((v) => (
                <VariableInput key={v.key} variable={v} value={variableValues[v.key]} onChange={handleVariableChange} onImageUpload={handleImageUploadForVar} />
              ))}
            </div>
          </ScrollArea>
          
          <div className="border rounded-lg overflow-hidden bg-gray-100">
            <div className="bg-gray-200 px-3 py-2 text-sm font-medium flex items-center gap-2"><Eye className="w-4 h-4" /> Vista Previa</div>
            <ScrollArea className="h-[460px]">
              <div className="p-4 bg-gray-50">
                <div className="bg-white shadow-sm rounded-lg overflow-hidden">
                  <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
                </div>
              </div>
            </ScrollArea>
          </div>
        </div>
        
        <div className="flex justify-end gap-2 pt-4 border-t mt-4">
          <Button variant="outline" onClick={() => setShowCustomizer(false)}>Cancelar</Button>
          <Button onClick={handleConfirm}><Check className="w-4 h-4 mr-2" /> Usar Esta Plantilla</Button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="h-full flex flex-col">
      <div className="pb-4 border-b">
        <div className="flex flex-wrap gap-2">
          <Button variant={selectedCategory === "all" ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory("all")}>
            Todas ({plantillas.length})
          </Button>
          {Object.entries(categories).map(([cat, items]) => {
            const info = CATEGORY_INFO[cat] || CATEGORY_INFO.otros;
            const Icon = info.icon;
            return (
              <Button key={cat} variant={selectedCategory === cat ? "default" : "outline"} size="sm" onClick={() => setSelectedCategory(cat)}>
                <Icon className="w-3 h-3 mr-1" />{info.label} ({items.length})
              </Button>
            );
          })}
        </div>
      </div>
      
      <ScrollArea className="flex-1 pt-4">
        <div className="grid grid-cols-3 gap-4 pb-4">
          {filteredPlantillas.map((template) => {
            const catInfo = CATEGORY_INFO[template.categoria] || CATEGORY_INFO.otros;
            return (
              <Card key={template.id} className="cursor-pointer hover:ring-2 hover:ring-blue-500 transition-all overflow-hidden group" onClick={() => handleSelectTemplate(template)}>
                <div className="h-32 overflow-hidden bg-gray-50 relative">
                  <TemplatePreview template={template} scale={0.25} />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                    <Button size="sm" className="opacity-0 group-hover:opacity-100 transition-opacity"><Eye className="w-3 h-3 mr-1" /> Ver</Button>
                  </div>
                </div>
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h4 className="font-medium text-sm truncate">{template.nombre}</h4>
                      <p className="text-xs text-gray-500 truncate">{template.descripcion}</p>
                    </div>
                    <Badge variant="secondary" className="shrink-0 text-xs">{catInfo.label}</Badge>
                  </div>
                  <div className="flex items-center gap-2 mt-2">
                    <div className="w-4 h-4 rounded-full border" style={{ backgroundColor: template.color_scheme || "#0C314C" }} />
                    <span className="text-xs text-gray-400">{template.variables?.length || 0} variables</span>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </ScrollArea>
      
      <div className="flex justify-end pt-4 border-t">
        <Button variant="outline" onClick={onClose}>Cancelar</Button>
      </div>
    </div>
  );
}
